import AppToast from "@/src/components/AppToast";
import { HttpError } from "@/src/util/HttpError";
import { RpcError } from "@/src/util/RpcError";
import { ToastIntent, ToastPosition } from "@fluentui/react-components";
import { useRouter } from "next/navigation";
import { JSX } from "react";
import { OTSError } from "./OTSError";

type ShowToast = (
  content: JSX.Element,
  options: {
    position: ToastPosition | undefined;
    intent: ToastIntent;
  },
  throttled?: boolean,
) => void;

type Translate = (key: string) => string;

const toastOptions: {
  position: ToastPosition | undefined;
  intent: ToastIntent;
} = { position: "bottom-end", intent: "error" };

export function handleRequestError(
  err: unknown,
  showToast: ShowToast,
  t: Translate,
  router?: ReturnType<typeof useRouter>,
) {
  console.log("[DEBUG] HandleRequestError", err);
  const toastTitle = (msg: string) => {
    // i18n keys stay as keys; raw gateway garbage is replaced upstream in getRpcError.
    try {
      return t(msg);
    } catch {
      return msg;
    }
  };

  if (err instanceof RpcError) {
    switch (err.httpStatus) {
      case 401:
      case 403:
        showToast(<AppToast title={toastTitle(err.message)} />, toastOptions, true);
        break;
      case 404:
        router?.push("/not-found");
        showToast(<AppToast title={toastTitle(err.message)} />, toastOptions, true);
        break;
      case 429:
      case 502:
      case 503:
      case 504:
        showToast(<AppToast title={toastTitle(err.message)} />, toastOptions, true);
        break;
      default:
        showToast(<AppToast title={toastTitle(err.message)} />, toastOptions);
        break;
    }
  } else if (err instanceof HttpError) {
    switch (err.errorCode) {
      case 401:
      case 403:
        break;
      case 404:
        router?.push("/not-found");
        break;
    }
    showToast(<AppToast title={toastTitle(err.message)} />, toastOptions);
  } else if (err instanceof OTSError) {
    switch (err.errorCode) {
      case 401:
        showToast(<AppToast title={err.message} />, toastOptions, true);
        break;
      case 404:
        router?.back();
        showToast(<AppToast title={err.message} />, toastOptions);
        break;
      default:
        showToast(<AppToast title={err.message} />, toastOptions);
        break;
    }
  } else if (typeof err === "string") {
    const isHtmlJson =
      /Unexpected token\s*['"`]?</i.test(err) ||
      /<\/?html[\s>]/i.test(err) ||
      /is not valid JSON/i.test(err);
    showToast(
      <AppToast
        title={isHtmlJson ? toastTitle("error.request.gateway_timeout") : err}
      />,
      toastOptions,
      isHtmlJson,
    );
  } else if (err instanceof Error) {
    const msg = err.message || "";
    const isHtmlJson =
      /Unexpected token\s*['"`]?</i.test(msg) ||
      /<\/?html[\s>]/i.test(msg) ||
      /is not valid JSON/i.test(msg);
    showToast(
      <AppToast
        title={
          isHtmlJson
            ? toastTitle("error.request.gateway_timeout")
            : msg || toastTitle("error.request.unknown_error")
        }
      />,
      toastOptions,
      isHtmlJson,
    );
  } else {
    showToast(<AppToast title={toastTitle("error.request.unknown_error")} />, toastOptions);
  }
}
